import * as React from "react";
import styled from "styled-components";
import { Container, Row, Col } from "react-bootstrap";
import { colors, WMKGradient } from "../../../vars/palette";
import { Slide } from "react-awesome-reveal";
import { BlockTwoColumn } from "../../../fragments/blockTwoColumnFields";
import { Img, WmkImage } from "wmk-image";
import { RichText, RichTextReact } from "wmk-rich-text";
import { options } from "../RichText/richTextOptions";
import HubspotForm from "react-hubspot-form";
import { altButtonStyles } from "../../routes/Solutions/ButtonStyles";

const Wrap = styled.div<{
  slug: string;
  title: string;
  whiteText?: boolean;
  secondaryTextColor?: string;
  hasFormStyling: boolean;
}>`
  margin: ${({ slug, title }) =>
    slug === "solutions" || title === "Solutions Page Hero" ? "0 0" : "0 0"};
  padding: ${({ title }) =>
    title === "Solutions Page Hero" ? "4rem 0" : "3rem 0"};
  h1,
  h2,
  h3,
  h4 {
    font-weight: bold;
  }
  ${({ whiteText }) => (whiteText ? `color: white !important;` : "")}
  ${({ title }) =>
    title === "Solutions Page Hero" &&
    `background: #332237; color: ${colors.hex(
      "white"
    )}; padding: 4rem 0; h1{margin-bottom: 1rem;}`};

  code {
    color: ${({ secondaryTextColor }) =>
      secondaryTextColor
        ? secondaryTextColor
        : colors.hex("primary")} !important;
    font-family: inherit;
    font-size: 100%;
  }
  a {
    display: inline-block;
    padding: 0.25rem 2.5rem;
    margin-top: 2rem;
    border: 2px solid ${colors.hex("secondary")};
    color: ${colors.hex("secondary")};
    text-decoration: none;
    transition: all 0.3s ease;
    text-align: center;
    font-family: Rajdhani;
    font-weight: 400;
    text-transform: uppercase;
    width: fit-content;
    &:hover {
      ${WMKGradient.grad()}
      color: white;
      border: 2px solid rgba(0, 0, 0, 0) !important;
      transition: all 0.3s ease;
    }
  }
  .embedded-image-wrap {
    .embedded-image {
      width: 100%;
      height: auto;
    }
  }
  @media only screen and (min-width: 992px) {
    ${({ hasFormStyling }) =>
      hasFormStyling
        ? `background: linear-gradient(to left, #F5F5F5 50%, transparent 50%);`
        : ""}
  }
`;
const ContentCol = styled(Col)<{
  hasFormStyling: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ hasFormStyling }) => (hasFormStyling ? `justify-content: start;` : "")}
  position: relative;
  h3 {
    code {
      margin-top: 0;
    }
  }
  @media only screen and (max-width: 991px) {
    text-align: center;
  }
`;
const AnimatedIconWrap = styled.div<{
  leftRight: "left" | "right";
  stackOrder: number;
}>`
  position: absolute;
  width: 100%;
  height: auto;
  z-index: -1;
  opacity: 0.7;
  // transform: scale(1.5);

  .gatsby-image-wrapper {
    ${({ leftRight }) =>
      leftRight
        ? leftRight === "left"
          ? "left: -15vw; top: -5rem;"
          : "right: -35vw; bottom: -8rem;"
        : null}
    position: relative;
    width: 75%;
    max-width: 20rem;
    height: auto;
    margin: auto;
  }
  @media only screen and (max-width: 991px) {
    // width: 50%;
    .gatsby-image-wrapper {
      width: 100%;
      ${({ leftRight, stackOrder }) =>
        leftRight && stackOrder
          ? leftRight === "left"
            ? stackOrder === 1
              ? "left: -15vw; top: -10rem;"
              : "left: 55vw; top: 5rem;"
            : stackOrder === 1 // leftRight === "right"
            ? "left: -10vw; top: -13rem;"
            : "right: -50vw; bottom: -5rem;"
          : null}
    }
  }
`;

export const SolutionsCtaWrap = styled.div`
  a {
    display: inline-block;
    padding: 0.25rem 2.5rem;
    margin-top: 2rem;
    border: 2px solid ${colors.hex("secondary")};
    color: ${colors.hex("secondary")};
    text-decoration: none;
    transition: all 0.3s ease;
    &:hover {
      ${WMKGradient.grad()}
      color: white;
      border: 2px solid rgba(0, 0, 0, 0) !important;
      transition: all 0.3s ease;
    }
  }
  @media only screen and (max-width: 991px) {
    display: flex;
    justify-content: center;
  }
`;

const FormWrap = styled.div`
  margin-left: 2rem;
  input,
  textarea {
    // width: 20rem;
    width: 100%;
    max-width: 95vw;
    border-radius: 0;
    border: 2px solid #bcb7b7;
    background: none;
    padding-left: 0.5rem;
    &:hover {
      border: 2px solid ${colors.hex("secondary")};
    }
  }
  .hs-submit {
    margin-top: 1rem;
    max-width: 20rem;
    input {
      ${altButtonStyles}
      text-align: center;
    }
  }
  ul {
    margin-bottom: 0;
    list-style-type: none;
    color: ${colors.hex("primary")};
  }
  .hs-error-msg {
    position: relative;
    left: -1rem;
    display: block;
    margin-bottom: -1rem;
    color: ${colors.hex("secondary")};
  }
  .hs-main-font-element {
    // "Please complete all required fields"
    position: relative;
    left: -1rem;
    color: ${colors.hex("secondary")};
  }
  .hs-recaptcha {
    display: none;
  }
  #label-firstname-2e313995-b8f4-4f29-8a8c-7b2096915e98 {
    display: block;
  }
  @media only screen and (max-width: 991px) {
    margin-left: 0;
    padding-top: 2rem;
  }
`;

// background: linear-gradient(to left, #F5F5F5 50%, transparent 50%);

type TwoColumnProps = {
  block: BlockTwoColumn;
  context?: { slug: string };
};

export const TwoColumn = ({ block, context }: TwoColumnProps) => {
  const leftCol = new RichText(block.leftColumn);
  const rightCol = new RichText(block.rightColumn);
  const leftColumnAnimatedBackgroundImage = new Img(
    block.leftColumnAnimatedBackgroundImage
  );
  const rightColumnAnimatedBackgroundImage = new Img(
    block.rightColumnAnimatedBackgroundImage
  );
  const whiteText = block.whiteText;
  const secondaryTextColor = block.secondaryTextColor;
  const leftColWidth = parseInt(block.leftColumnWidth);
  const leftWidth = leftColWidth ? leftColWidth : 6;
  const rightColWidth = parseInt(block.rightColumnWidth);
  const rightWidth = rightColWidth ? rightColWidth : 6;
  const spacerWidth =
    leftColWidth && rightColWidth && leftColWidth + rightColWidth < 12
      ? 12 - (leftColWidth + rightColWidth)
      : null; // if the widths for both columns adds up to less than 12, take the remaining width and apply it to a spacer column in between the left and right columns
  const stackOrder = block.stackOrder;
  const leftStackOrder =
    stackOrder && stackOrder === "Left Column on top" ? 1 : 2;
  const rightStackOrder =
    stackOrder && stackOrder === "Right Column on top" ? 1 : 2;
  const title = block.title;
  const slug = context?.slug;
  return (
    <Wrap
      whiteText={whiteText}
      secondaryTextColor={secondaryTextColor}
      slug={slug}
      title={title}
      hasFormStyling={block?.replaceRightColumnWithForm}
    >
      <Container>
        <Row>
          <ContentCol
            xs={{ span: 12, order: leftStackOrder }}
            lg={{ span: leftWidth, order: 1 }}
            className="left-col"
          >
            <RichTextReact content={leftCol} options={options} />
            {leftColumnAnimatedBackgroundImage && (
              <AnimatedIconWrap leftRight="left" stackOrder={leftStackOrder}>
                <Slide direction="left">
                  {/* <Img
                    fluid={get(leftColumnAnimatedBackgroundImage, "fluid")}
                    alt={get(leftColumnAnimatedBackgroundImage, "title")}
                  /> */}
                  <WmkImage image={leftColumnAnimatedBackgroundImage} />
                </Slide>
              </AnimatedIconWrap>
            )}
          </ContentCol>
          {spacerWidth && <Col lg={{ span: spacerWidth, order: 2 }} />}
          <ContentCol
            xs={{
              span: 12,
              order: rightStackOrder
            }}
            lg={{ span: rightWidth, order: 3 }}
            className="right-col"
            hasFormStyling={block?.replaceRightColumnWithForm}
          >
            {block.replaceRightColumnWithForm ? (
              <FormWrap>
                <HubspotForm
                  portalId={"526959"}
                  formId={block?.rightColumnFormHubspotId}
                  onSubmit={() => {}}
                  onReady={() => {}}
                />
              </FormWrap>
            ) : (
              <>
                <RichTextReact content={rightCol} options={options} />
                {rightColumnAnimatedBackgroundImage && (
                  <AnimatedIconWrap
                    leftRight="right"
                    stackOrder={rightStackOrder}
                  >
                    <Slide direction="right">
                      <WmkImage image={rightColumnAnimatedBackgroundImage} />
                    </Slide>
                  </AnimatedIconWrap>
                )}
              </>
            )}
          </ContentCol>
        </Row>

        {title === "Solutions Page Hero" && (
          <Row>
            <Col>
              <SolutionsCtaWrap>
                <a href="#solutions-form">GET STARTED</a>
              </SolutionsCtaWrap>
            </Col>
          </Row>
        )}
      </Container>
    </Wrap>
  );
};

// {leftColumnAnimatedBackgroundImage && (
//   <AnimatedIconWrap leftRight="left" stackOrder={leftStackOrder}>
//     <Slide direction="left">
//       {/* <Img
//         fluid={get(leftColumnAnimatedBackgroundImage, "fluid")}
//         alt={get(leftColumnAnimatedBackgroundImage, "title")}
//       /> */}
//       <WmkImage image={leftColumnAnimatedBackgroundImage} />
//     </Slide>
//   </AnimatedIconWrap>
// )}

// {rightColumnAnimatedBackgroundImage && (
//   <AnimatedIconWrap leftRight="right" stackOrder={rightStackOrder}>
//     <Slide direction="right">
//       {/* <Img
//         fluid={get(rightColumnAnimatedBackgroundImage, "fluid")}
//         alt={get(rightColumnAnimatedBackgroundImage, "title")}
//       /> */}
//       <WmkImage image={rightColumnAnimatedBackgroundImage} />
//     </Slide>
//   </AnimatedIconWrap>
// )}
